import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../common/DataTable';
import StatusBadge from '../common/StatusBadge';
import { CHECK_TYPES, VERIFICATION_STATUSES } from '../../utils/constants';
import './VerificationDashboard.css';

const VerificationDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('MY_QUEUE');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Mock stats
  const stats = { pending: 12, assignedToMe: 5, attentionRequired: 2, completedToday: 8 };

  // Mock tasks
  const mockTasks = [
    { id: '1', applicationId: 'APP-001', applicantName: 'John Doe', type: CHECK_TYPES.IDENTITY, status: VERIFICATION_STATUSES.PENDING, assignedTo: 'me', ageDays: 1 },
    { id: '2', applicationId: 'APP-002', applicantName: 'Jane Smith', type: CHECK_TYPES.INCOME, status: VERIFICATION_STATUSES.ATTENTION_REQUIRED, assignedTo: 'me', ageDays: 2 },
    { id: '3', applicationId: 'APP-003', applicantName: 'Alice Johnson', type: CHECK_TYPES.CREDIT, status: VERIFICATION_STATUSES.PENDING, assignedTo: null, ageDays: 0 }
  ];

  const filteredTasks = mockTasks.filter(task => {
    if (activeTab === 'MY_QUEUE' && task.assignedTo !== 'me') return false;
    if (activeTab === 'UNASSIGNED' && task.assignedTo !== null) return false;
    if (typeFilter && task.type !== typeFilter) return false;
    if (statusFilter && task.status !== statusFilter) return false;
    return true;
  });

  const columns = [
    { header: 'App ID', accessor: 'applicationId' },
    { 
      header: 'Applicant', 
      accessor: 'applicantName',
      render: (row) => (
        <span 
          className="task-link" 
          onClick={() => navigate(`/verification/${row.id}`)}
          role="button"
          tabIndex={0}
        >
          {row.applicantName}
        </span>
      )
    },
    { header: 'Type', accessor: 'type', render: (row) => <span className="type-label">{row.type.replace('_', ' ')}</span> },
    { header: 'Status', accessor: 'status', render: (row) => <StatusBadge status={row.status} /> },
    { header: 'Age (Days)', accessor: 'ageDays' }
  ];

  return (
    <div className="verification-dashboard">
      <div className="verification-header">
        <h1>Verification Hub</h1>
        <div className="auto-refresh">
          <span className="refresh-indicator"></span> Auto-refreshing
        </div>
      </div>

      <div className="verification-stats">
        <div className="stat-card">
          <h3>My Queue</h3>
          <p className="stat-value">{stats.assignedToMe}</p>
        </div>
        <div className="stat-card">
          <h3>Attention Required</h3>
          <p className="stat-value warning">{stats.attentionRequired}</p>
        </div>
        <div className="stat-card">
          <h3>Unassigned Pending</h3>
          <p className="stat-value">{stats.pending}</p>
        </div>
        <div className="stat-card">
          <h3>Completed Today</h3>
          <p className="stat-value success">{stats.completedToday}</p>
        </div>
      </div>

      <div className="card dashboard-tasks">
        <div className="card-header dashboard-tasks__header">
          <div className="tabs">
            <button className={`tab ${activeTab === 'MY_QUEUE' ? 'active' : ''}`} onClick={() => setActiveTab('MY_QUEUE')}>My Queue</button>
            <button className={`tab ${activeTab === 'UNASSIGNED' ? 'active' : ''}`} onClick={() => setActiveTab('UNASSIGNED')}>Unassigned</button>
            <button className={`tab ${activeTab === 'ALL' ? 'active' : ''}`} onClick={() => setActiveTab('ALL')}>All Tasks</button>
          </div>
          {activeTab === 'UNASSIGNED' && (
            <button className="btn-primary btn-sm">Bulk Assign to Me</button>
          )}
        </div>
        
        <div className="card-body">
          <div className="task-filters">
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="filter-select">
              <option value="">All Check Types</option>
              {Object.values(CHECK_TYPES).map(type => (
                <option key={type} value={type}>{type.replace('_', ' ').toUpperCase()}</option>
              ))}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select">
              <option value="">All Statuses</option>
              {Object.values(VERIFICATION_STATUSES).map(status => (
                <option key={status} value={status}>{status.replace('_', ' ').toUpperCase()}</option>
              ))}
            </select>
          </div>

          <div className="desktop-view">
            <DataTable columns={columns} data={filteredTasks} emptyMessage="No tasks found." />
          </div>

          <div className="mobile-view">
            {filteredTasks.length > 0 ? (
              filteredTasks.map(task => (
                <div key={task.id} className="mobile-task-card" onClick={() => navigate(`/verification/${task.id}`)}>
                  <div className="mobile-task-header">
                    <strong>{task.applicantName}</strong>
                    <StatusBadge status={task.status} />
                  </div>
                  <div className="mobile-task-details">
                    <span>{task.type.replace('_', ' ')}</span>
                    <span>App: {task.applicationId}</span>
                  </div>
                </div>
              ))
            ) : (
              <p className="empty-state">No tasks found.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationDashboard;
